(function (root) {
  'use strict';
  function grams(title) {
    for (const part of String(title).toLowerCase().split(' / ')) {
      const value = part.replace(/grams?/g, 'g').replace(/ounces?/g, 'oz')
        .replace(/⅛|1\/8/g, '0.125').replace(/¼|1\/4/g, '0.25').replace(/½|1\/2/g, '0.5')
        .replace(/smalls|\s|[()]/g, '');
      const match = value.match(/^(\d+(?:\.\d+)?|\.\d+)(g|oz)$/);
      if (match && +match[1] > 0) return +match[1] * (match[2] === 'oz' ? 28 : 1);
    }
    return 0;
  }
  function label(title, weight = grams(title)) {
    const base = ({3.5:'⅛ oz',7:'¼ oz',14:'½ oz',28:'1 oz',56:'2 oz'})[weight] || (weight ? weight + ' g' : title);
    return base + (weight && /smalls/i.test(title) ? ' · Smalls' : '');
  }
  function unitMinor(priceMinor, weight) { return weight > 0 ? Math.round(priceMinor / weight) : null; }
  function formatMoney(amount, currency = 'USD', locale = 'en-US') {
    const rounded = Math.round(amount * 100) / 100;
    return new Intl.NumberFormat(locale, {style:'currency', currency, minimumFractionDigits:Number.isInteger(rounded) ? 0 : 2, maximumFractionDigits:2}).format(rounded);
  }
  function bestValue(variants) {
    const weighted = variants.map(variant => ({...variant, grams:grams(variant.title)})).filter(variant => variant.grams > 0);
    if (weighted.length < 2) return null;
    const available = weighted.filter(variant => variant.available && Number.isFinite(variant.price) && variant.price >= 0);
    available.sort((a,b) => a.price/a.grams - b.price/b.grams || b.grams-a.grams);
    return available[0]?.id || null;
  }
  function savingsBase(variants) {
    return variants.map(variant => ({...variant, grams:grams(variant.title)}))
      .filter(variant => variant.available && variant.grams > 0 && Number.isFinite(variant.price) && variant.price >= 0)
      .sort((a,b) => a.grams-b.grams || a.price-b.price)[0] || null;
  }
  function savingsMinor(variant, variants, optionCount = 1) {
    const base = savingsBase(variants), weight = grams(variant.title);
    if (!variant.available || !base || optionCount !== 1 || weight <= base.grams || !Number.isFinite(variant.price)) return 0;
    return Math.max(0, Math.round(base.price * 100 * weight / base.grams - variant.price * 100));
  }
  function savingsLabel(variant, variants, optionCount = 1) {
    const saving = savingsMinor(variant, variants, optionCount), base = savingsBase(variants);
    if (!saving || !base) return '';
    const count = grams(variant.title) / base.grams;
    return 'You save ' + formatMoney(saving / 100) + ' vs ' + (Number.isInteger(count) ? count + ' × ' + label(base.title) : 'the ' + label(base.title) + ' price/g');
  }
  function compactLabel(title) {
    const weight = grams(title);
    return (({3.5:'⅛',7:'¼',14:'½'})[weight] || label(title).replace(' · Smalls','')) + (/smalls/i.test(title) ? ' S' : '');
  }
  const api = {grams, label, compactLabel, unitMinor, bestValue, formatMoney, savingsBase, savingsMinor, savingsLabel};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.DGPhotoWeights = api;
  if (typeof document === 'undefined' || root.DGPhotoWeightsReady) return;
  root.DGPhotoWeightsReady = true;
  const activeInputs = new WeakMap(), tapAnimations = new WeakMap();
  function unavailable(option) {
    return !!(option && (option.disabled || option.dataset.available === 'false' || option.dataset.photoAvailable === 'false' || option.getAttribute?.('aria-disabled') === 'true'));
  }
  function tapUnavailable(option) {
    const tile = option.querySelector?.('.edition-tile') || option;
    if (root.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches || !tile.animate) return;
    tapAnimations.get(tile)?.cancel();
    tapAnimations.set(tile, tile.animate([{transform:'translateX(0)'},{transform:'translateX(-3px)'},{transform:'translateX(3px)'},{transform:'translateX(-2px)'},{transform:'translateX(0)'}], {duration:260,easing:'ease-out'}));
  }
  function sync(scope, input) {
    if (!input || !scope) return;
    if (!unavailable(input)) activeInputs.set(scope, input);
    scope.querySelectorAll('[data-photo-summary]').forEach(summary => {
      for (const key of ['price','label','grams','unit','savings']) {
        const node = summary.querySelector('[data-photo-' + key + ']');
        if (!node) continue;
        const value = input.dataset['photo' + key[0].toUpperCase() + key.slice(1)] || '';
        if (node.textContent !== value) node.textContent = value;
        node.hidden = !value;
      }
    });
  }
  function selectCard(card, choice) {
    if (!card || !choice || unavailable(choice)) return;
    card.querySelectorAll('[data-photo-card-variant]').forEach(button => button.setAttribute('aria-pressed', String(button === choice)));
    const segments = choice.closest('[data-photo-segments]');
    if (segments) {
      const choices = [...segments.querySelectorAll('[data-photo-card-variant]')];
      segments.style.setProperty('--photo-active-index', choices.indexOf(choice));
      segments.dataset.photoHasSelection = 'true';
    }
    const price = card.querySelector('.card-price-stack > .price');
    const unit = card.querySelector('.card-price-stack .unit-price-line');
    if (price) price.textContent = choice.dataset.photoPrice;
    if (unit) { unit.textContent = choice.dataset.photoUnit; unit.title = choice.getAttribute('aria-label'); }
    card.querySelectorAll('.product-photo,.name-row h2 a,.card-bottom > a,[data-photo-card-view]').forEach(link => link.href = choice.dataset.photoUrl);
    const form = card.querySelector('[data-photo-card-form]');
    if (form) {
      const variant = form.querySelector('[name="id"]');
      const quantity = form.querySelector('[name="quantity"]');
      if (variant && variant.value !== choice.dataset.photoCardVariant) {
        variant.value = choice.dataset.photoCardVariant;
        if (quantity) quantity.value = String(Number(choice.dataset.photoMinimum) || 1);
        const button = form.querySelector('[name="add"]');
        if (button && !button.hasAttribute('aria-busy')) button.disabled = false;
        form.querySelectorAll('[data-form-error],[data-stock-message]').forEach(message => { message.hidden = true; message.textContent = ''; });
      }
    }
    const quick = card.querySelector('[data-quick-template],[data-quick]');
    if (quick) quick.dataset.photoQuickVariant = choice.dataset.photoCardVariant;
  }
  function init(rootNode) {
    if (!rootNode.querySelectorAll) return;
    const scopes = [...rootNode.querySelectorAll('[data-product-scope]')];
    if (rootNode.matches?.('[data-product-scope]')) scopes.push(rootNode);
    scopes.forEach(scope => {
      let input = scope.querySelector('input[data-photo-label]:checked');
      if (input && unavailable(input)) {
        const fallback = [...scope.querySelectorAll('input[data-photo-label]')].find(candidate => !unavailable(candidate));
        if (fallback) {
          input.checked = false;
          fallback.checked = true;
          input = fallback;
          queueMicrotask(() => fallback.dispatchEvent(new Event('change', {bubbles:true})));
        }
      }
      sync(scope, input);
    });
    const cards = [...rootNode.querySelectorAll('.product-card')];
    if (rootNode.matches?.('.product-card')) cards.push(rootNode);
    cards.forEach(card => {
      const available = [...card.querySelectorAll('[data-photo-card-variant]')].filter(choice => !unavailable(choice));
      selectCard(card, available.find(choice => choice.getAttribute('aria-pressed') === 'true') || available[0]);
    });
  }
  document.addEventListener('click', event => {
    const option = event.target.closest('.photo-option--sold,[data-photo-card-variant][aria-disabled="true"]');
    if (!option) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    tapUnavailable(option);
  }, true);
  document.addEventListener('change', event => {
    const input = event.target.closest('input[data-photo-label]');
    if (!input || !unavailable(input)) return;
    const scope = input.closest('[data-product-scope]'), active = scope && activeInputs.get(scope);
    input.checked = false;
    if (active && !unavailable(active)) active.checked = true;
    else if (scope) {
      const fallback = [...scope.querySelectorAll('input[data-photo-label]')].find(candidate => !unavailable(candidate));
      if (fallback) {
        fallback.checked = true;
        queueMicrotask(() => fallback.dispatchEvent(new Event('change', {bubbles:true})));
      }
    }
    event.stopImmediatePropagation();
  }, true);
  document.addEventListener('change', event => {
    const input = event.target.closest('input[data-photo-label]');
    if (input && !unavailable(input)) queueMicrotask(() => sync(input.closest('[data-product-scope]'), input));
  });
  document.addEventListener('keydown', event => {
    if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
    const choice = event.target.closest('[data-photo-card-variant]');
    const segments = choice?.closest('[data-photo-segments]');
    if (!segments) return;
    const choices = [...segments.querySelectorAll('[data-photo-card-variant]')].filter(button => !unavailable(button));
    if (!choices.length) return;
    event.preventDefault();
    const index = choices.indexOf(choice);
    const next = event.key === 'Home' ? choices[0] : event.key === 'End' ? choices[choices.length - 1] : choices[(index + (event.key === 'ArrowRight' ? 1 : -1) + choices.length) % choices.length];
    selectCard(next.closest('.product-card'), next);
    next.focus();
  });
  document.addEventListener('click', event => {
    const choice = event.target.closest('[data-photo-card-variant]');
    if (choice) selectCard(choice.closest('.product-card'), choice);
    const quick = event.target.closest('[data-photo-quick-variant]');
    if (quick) queueMicrotask(() => {
      const dialog = document.querySelector('dialog.quick-view[open],#quick-view[open],#dg-quick-view[open]');
      const input = [...(dialog?.querySelectorAll('input[data-photo-label]') || [])].find(node => node.value === quick.dataset.photoQuickVariant);
      if (input && !unavailable(input)) { input.checked = true; input.dispatchEvent(new Event('change', {bubbles:true})); }
    });
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  function start() {
    init(document);
    new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => {
      if (node.nodeType === 1) init(node);
    }))).observe(document.body, {childList:true, subtree:true});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true}); else start();
})(typeof window === 'undefined' ? globalThis : window);
